import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Play, Search, ServerCog, XCircle } from "lucide-react";

import { Layout } from "@/components/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type ApiEndpoint, type GatewayEndpointProbe, loadApiEndpoints, probeGatewayEndpoint } from "@/lib/openApi";

type TestResult = {
  ok: boolean;
  durationMs: number;
  message: string;
  data?: GatewayEndpointProbe;
};

type EndpointSection = {
  key: string;
  label: string;
  routeCount: number;
  operationCount: number;
};

type ComplianceItemProps = {
  label: string;
  ok: boolean;
  expected: string;
  actual: string;
};

function ComplianceItem({ label, ok, expected, actual }: ComplianceItemProps) {
  return (
    <div className={`rounded-md border p-3 ${ok ? "border-emerald-200 bg-emerald-50/70" : "border-rose-200 bg-rose-50/70"}`}>
      <div className={`flex items-center gap-2 text-sm font-semibold ${ok ? "text-emerald-800" : "text-rose-800"}`}>
        {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        {label} · {ok ? "Conforme" : "Divergente"}
      </div>
      <dl className="mt-2 space-y-1 text-xs text-foreground">
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">Esperado</dt>
          <dd className="break-all font-mono">{expected}</dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">Recebido</dt>
          <dd className="break-all font-mono">{actual || "Não retornado"}</dd>
        </div>
      </dl>
    </div>
  );
}

const getSection = (path: string) => path.startsWith("/api/clients/code/__")
  ? "temporarios"
  : path.split("/").filter(Boolean)[1] ?? "outros";

const formatSectionLabel = (section: string) => section === "temporarios"
  ? "Temporários"
  : section
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getEndpointVariant = (path: string) => {
  if (path === "/api/scheduled-orders" || path === "/api/scheduled-orders/") {
    return { label: "Oficial", classes: "bg-emerald-100 text-emerald-800" };
  }
  return undefined;
};

const methodClasses: Record<string, string> = {
  GET: "bg-sky-100 text-sky-800",
  POST: "bg-emerald-100 text-emerald-800",
  PUT: "bg-amber-100 text-amber-800",
  PATCH: "bg-violet-100 text-violet-800",
  DELETE: "bg-rose-100 text-rose-800",
};

export default function ApiDiagnostics() {
  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [selectedSection, setSelectedSection] = useState("");
  const [search, setSearch] = useState("");
  const [runningPath, setRunningPath] = useState<string>();
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [expandedResults, setExpandedResults] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    loadApiEndpoints(controller.signal)
      .then(setEndpoints)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadError(error instanceof Error ? error.message : "Falha ao carregar o catálogo de endpoints.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const sections = useMemo<EndpointSection[]>(() => {
    const grouped = new Map<string, ApiEndpoint[]>();
    endpoints.forEach((endpoint) => {
      const section = getSection(endpoint.path);
      grouped.set(section, [...(grouped.get(section) ?? []), endpoint]);
    });
    return [...grouped.entries()]
      .map(([key, items]) => ({
        key,
        label: formatSectionLabel(key),
        routeCount: items.length,
        operationCount: items.reduce((total, item) => total + item.methods.length, 0),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [endpoints]);

  const visibleEndpoints = useMemo(
    () => selectedSection
      ? endpoints.filter((endpoint) => getSection(endpoint.path) === selectedSection)
      : endpoints,
    [endpoints, selectedSection],
  );

  const filteredEndpoints = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return visibleEndpoints;
    return visibleEndpoints.filter((endpoint) =>
      endpoint.path.toLowerCase().includes(term)
      || endpoint.methods.some((method) => method.toLowerCase().includes(term)),
    );
  }, [visibleEndpoints, search]);

  const selectedSectionData = sections.find((section) => section.key === selectedSection);

  const displayedGroups = useMemo(() => sections
    .map((section) => ({
      ...section,
      endpoints: filteredEndpoints.filter((endpoint) => getSection(endpoint.path) === section.key),
    }))
    .filter((section) => section.endpoints.length > 0), [filteredEndpoints, sections]);

  useEffect(() => {
    setExpandedSections(displayedGroups.map((group) => group.key));
  }, [selectedSection, displayedGroups]);

  const runConnectionTest = async (endpoint: ApiEndpoint, method: string) => {
    const resultKey = `${method} ${endpoint.path}`;
    setRunningPath(resultKey);
    const startedAt = performance.now();
    try {
      const gatewayProbe = await probeGatewayEndpoint(endpoint.path, method);
      setResults((current) => ({
        ...current,
        [resultKey]: {
          ok: gatewayProbe.success,
          durationMs: Math.round(performance.now() - startedAt),
          message: gatewayProbe.message,
          data: gatewayProbe,
        },
      }));
    } catch (error) {
      setResults((current) => ({
        ...current,
        [resultKey]: {
          ok: false,
          durationMs: Math.round(performance.now() - startedAt),
          message: error instanceof Error ? error.message : "Falha ao avaliar a conexão.",
        },
      }));
    } finally {
      setRunningPath(undefined);
    }
  };

  const handleSectionChange = (section: string) => {
    setSelectedSection(section === "all" ? "" : section);
  };

  return (
    <Layout title="Diagnóstico de APIs">
      <div className="space-y-6">
        <Card className="overflow-hidden border-primary/20 shadow-sm">
          <div className="h-1 bg-primary" />
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <ServerCog className="h-6 w-6 text-primary" />
              Teste de CORS
            </CardTitle>
            <CardDescription className="max-w-3xl leading-relaxed">
              Verifique as rotas publicadas no API Gateway. GET realiza uma leitura real; métodos mutáveis são validados com OPTIONS sem executar a operação de negócio.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label htmlFor="endpoint-section" className="text-sm font-semibold">Seção da API</label>
            <Select value={selectedSection || "all"} onValueChange={handleSectionChange} disabled={loading || !!loadError}>
              <SelectTrigger id="endpoint-section" className="max-w-xl bg-background">
                <SelectValue placeholder={loading ? "Carregando seções..." : "Todas as seções"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as seções — {endpoints.length} rotas</SelectItem>
                {sections.map((section) => (
                  <SelectItem key={section.key} value={section.key}>
                    {section.label} — {section.routeCount} rotas / {section.operationCount} operações
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative max-w-xl pt-2">
              <Search className="absolute left-3 top-[18px] h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Pesquisar endpoint ou método em todas as seções"
                className="pl-9"
                disabled={loading || !!loadError}
              />
            </div>
          </CardContent>
        </Card>

        {loading && (
          <Card><CardContent className="flex items-center gap-3 py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-primary" /> Carregando catálogo de endpoints...
          </CardContent></Card>
        )}

        {loadError && (
          <Card className="border-destructive/50 bg-destructive/5"><CardContent className="flex items-start gap-3 py-6 text-destructive">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div><p className="font-semibold">Não foi possível carregar os endpoints</p><p className="mt-1 text-sm">{loadError}</p></div>
          </CardContent></Card>
        )}

        {!loading && !loadError && (
          <section className="space-y-4" aria-labelledby="selected-section-title">
            <Card><CardContent className="py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  {selectedSectionData ? "Seção selecionada" : "Catálogo completo"}
                </p>
                <h2 id="selected-section-title" className="mt-1 text-xl font-semibold">
                  {selectedSectionData?.label ?? "Todas as seções"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {filteredEndpoints.length} de {visibleEndpoints.length} rotas exibidas
                </p>
              </div>
            </CardContent></Card>

            <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="space-y-3">
              {displayedGroups.map((group) => (
              <AccordionItem key={group.key} value={group.key} className="overflow-hidden rounded-lg border bg-card px-0 shadow-sm">
                <AccordionTrigger className="px-5 py-4 hover:bg-muted/30 hover:no-underline">
                  <div className="flex flex-1 items-center justify-between gap-4 pr-4 text-left">
                    <div>
                      <h3 className="text-lg font-semibold">{group.label}</h3>
                      <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                        Endpoints publicados neste domínio da API
                      </p>
                    </div>
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                      {group.endpoints.length} {group.endpoints.length === 1 ? "rota" : "rotas"}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 border-t bg-muted/10 p-3 sm:p-4">
                {group.endpoints.map((endpoint) => (
              <Card key={endpoint.path} className="transition-shadow hover:shadow-md">
                <CardHeader className="border-b bg-muted/20 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="break-all font-mono text-base">{endpoint.path}</CardTitle>
                    {getEndpointVariant(endpoint.path) && (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getEndpointVariant(endpoint.path)!.classes}`}>
                        {getEndpointVariant(endpoint.path)!.label}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="divide-y p-0">
                  {endpoint.methods.map((method) => {
                    const resultKey = `${method} ${endpoint.path}`;
                    const result = results[resultKey];
                    const isRunning = runningPath === resultKey;
                    const isExpanded = expandedResults[resultKey];
                    return (
                      <div key={method} className="p-4 sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <span className={`w-fit rounded-md px-2.5 py-1 text-xs font-bold ${methodClasses[method] ?? "bg-muted text-foreground"}`}>{method}</span>
                          <Button type="button" variant="outline" size="sm" disabled={runningPath !== undefined}
                            onClick={() => runConnectionTest(endpoint, method)}>
                            {isRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                            Avaliar Conexão
                          </Button>
                        </div>

                        {result && (
                          <div className={`mt-4 rounded-md border p-4 ${result.ok ? "border-emerald-200 bg-emerald-50" : "border-destructive/30 bg-destructive/5"}`}>
                            <div className={`flex items-start gap-3 ${result.ok ? "text-emerald-800" : "text-destructive"}`}>
                              {result.ok ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0" />}
                              <div className="flex-1">
                                <p className="font-semibold">{result.ok ? "Gateway conforme" : "Gateway não validado"}</p>
                                <p className="mt-1 text-sm">{result.message} · {result.durationMs} ms</p>
                              </div>
                            </div>
                            {result.data && (
                              <div className="mt-3 border-t border-current/10 pt-3">
                                <div className="mb-3 grid gap-3 lg:grid-cols-2">
                                  <ComplianceItem
                                    label="Origin"
                                    ok={result.data.originMatches}
                                    expected={result.data.expectedOrigin}
                                    actual={result.data.actualOrigin ?? ""}
                                  />
                                  <ComplianceItem
                                    label="Credentials"
                                    ok={result.data.credentialsMatch}
                                    expected={result.data.expectedCredentials}
                                    actual={result.data.actualCredentials ?? ""}
                                  />
                                  <ComplianceItem
                                    label="Method"
                                    ok={result.data.methodAllowed}
                                    expected={result.data.method}
                                    actual={result.data.allowedMethods.join(", ")}
                                  />
                                  <ComplianceItem
                                    label="Headers"
                                    ok={result.data.requiredHeadersAllowed}
                                    expected="authorization, content-type"
                                    actual={result.data.allowedHeaders.join(", ")}
                                  />
                                </div>
                                <p className="mb-2 text-xs text-muted-foreground">
                                  Preflight HTTP {result.data.status} em <span className="break-all font-mono">{result.data.materializedPath}</span>
                                </p>
                                <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs"
                                  onClick={() => setExpandedResults((current) => ({ ...current, [resultKey]: !current[resultKey] }))}>
                                  {isExpanded ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
                                  {isExpanded ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
                                </Button>
                                {isExpanded && <pre className="mt-3 max-h-64 overflow-auto rounded bg-background/80 p-3 text-xs text-foreground">
                                  {JSON.stringify(result.data, null, 2)}
                                </pre>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
                ))}
                </AccordionContent>
              </AccordionItem>
              ))}
            </Accordion>

            {filteredEndpoints.length === 0 && (
              <Card className="border-dashed"><CardContent className="py-10 text-center text-sm text-muted-foreground">
                Nenhum endpoint encontrado para o filtro informado.
              </CardContent></Card>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}
